import {getApolloClient} from "@/shared/apolloclient/apollo.client.ts";
import {Account, App, CheckoutInput, CheckoutResponse, Tenant, TenantSettings} from "@/shared/graphql/types";
import {extractGraphData} from "@/shared/composable/service.composables.ts";
import type {DocumentNode} from "graphql/index";
import type {TypedDocumentNode} from "@graphql-typed-document-node/core";
import type {OperationVariables} from "@apollo/client/core/types";
import {OnBehalfOf} from "@/shared/composable/security.ts";
//@ts-ignore
import LOAD_CHECKOUT_QUERY from "@/checkout/graphql/checkout.query.graphql";
//@ts-ignore
import CANCEL_CHECKOUT_QUERY from "@/checkout/graphql/checkout.cancel.mutation.graphql";
//@ts-ignore
import COMPLETE_CHECKOUT_MUTATION from "@/checkout/graphql/checkout.complete.mutation.graphql";
//@ts-ignore
import UPDATE_CHECKOUT_MUTATION from "@/checkout/graphql/checkout.update.mutation.graphql";
import {activeLanguage} from "@/checkout/i18n/i18n.ts";

export type CheckoutInit = {
    checkoutResponse: CheckoutResponse
    account?: Account
    tenantSettings: TenantSettings
    app: App
}

class CheckoutService {
    async init(checkoutId: string, onBehalfOf?: OnBehalfOf | null): Promise<CheckoutInit> {
        const response = await this.executeQuery<Tenant>(LOAD_CHECKOUT_QUERY, {
            lang: activeLanguage(),
            id: checkoutId
        }, onBehalfOf);
        return {
            checkoutResponse: response.checkout,
            account: response.me,
            tenantSettings: response.settings,
            app: response.app,
        } as CheckoutInit
    }

    async cancel(checkoutId: string, onBehalfOf?: OnBehalfOf | null): Promise<boolean> {
        return await this.executeMutation<boolean>(CANCEL_CHECKOUT_QUERY, {
            lang: activeLanguage(),
            id: checkoutId,
        }, onBehalfOf);
    }

    async update(checkoutId: string, input: CheckoutInput, onBehalfOf?: OnBehalfOf | null) {
        return await this.executeMutation<CheckoutResponse>(UPDATE_CHECKOUT_MUTATION, {
            lang: activeLanguage(),
            id: checkoutId,
            input: input,
        }, onBehalfOf);
    }

    async complete(checkoutId: string, input: CheckoutInput, returnUrl: string, onBehalfOf?: OnBehalfOf | null) {
        return await this.executeMutation<CheckoutResponse>(COMPLETE_CHECKOUT_MUTATION, {
            lang: activeLanguage(),
            id: checkoutId,
            input: input,
            returnUrl: returnUrl,
        }, onBehalfOf);
    }

    private async executeQuery<T>(query: DocumentNode | TypedDocumentNode, variables: OperationVariables, onBehalfOf?: OnBehalfOf | null): Promise<T> {
        try {
            const response = await getApolloClient().query({
                query: query,
                variables: {
                    ...variables,
                    "imageProfile": "product",
                },
                fetchPolicy: 'network-only',
                context: {"OnBehalfOf": onBehalfOf}
            });
            return extractGraphData(response?.data);
        } catch (e) {
            throw e;
        }
    }

    private async executeMutation<T>(mutation: DocumentNode | TypedDocumentNode, variables: OperationVariables, onBehalfOf?: OnBehalfOf | null): Promise<T> {
        try {
            const response = await getApolloClient().mutate({
                mutation: mutation,
                variables: {
                    ...variables,
                    "imageProfile": "product",
                },
                fetchPolicy: 'network-only',
                context: {"OnBehalfOf": onBehalfOf}
            });
            return extractGraphData(response?.data);
        } catch (e) {
            throw e;
        }
    }
}

export const checkoutService = new CheckoutService();
export default checkoutService;